"use client";

import { Skeleton } from "@/components/skeleton";
import { cn } from "@/utils/classnames";
import { Avatar, Icon } from "@stone-payments/jade";

type Sizes = "medium" | "large" | "xlarge";

type Props = {
  value?: string;
  loading?: boolean;
  size?: Sizes;
  border?: boolean;
  img?: string;
  alt?: string;
};

const BrandSymbolLoading = ({ className, ...rest }: { className?: string }) => (
  <div className={className} {...rest}>
    <Skeleton width="w-full" height="h-full" />
  </div>
);

const BrandSymbolWithValue = ({
  label,
  size,
  className,
}: {
  label: string;
  size: Sizes;
  className: string;
}) => {
  const words = label
    .replace(/[^\p{L}\s]+/gu, "")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ");

  const value =
    words.length === 1
      ? words[0]
      : words
          .map((word) => word[0])
          .join("")
          .slice(0, 2);

  return value ? (
    <Avatar className={className} label={value.toUpperCase()} size={size} />
  ) : (
    <Icon use="shop" size={size} color="brand" />
  );
};

export const BrandSymbol = ({
  value,
  loading = false,
  size = "medium",
  border = false,
  img,
  alt,
  ...rest
}: Props) => {
  const customBackgroundClassname =
    "!bg-[var(--theme-color-background-surface)] bg-[linear-gradient(0deg,var(--theme-color-background-brand-subtle)_0%,var(--theme-color-background-brand-subtle)_100%)]";
  const classes = cn(`jade-avatar overflow-hidden is-${size} !bg-white`, {
    "border border-2 border-white shadow-[0_5px_20px_0_rgba(0,0,45,0.09)]":
      border,
  });

  if (loading) return <BrandSymbolLoading className={classes} {...rest} />;

  return (
    <div className={cn(classes, customBackgroundClassname)} {...rest}>
      {img ? <Avatar size={size} as="img" src={img} alt={alt} /> : null}

      {value && !img ? (
        <BrandSymbolWithValue label={value!} size={size} className={classes} />
      ) : null}

      {!img && !value ? <Icon use="shop" size={size} color="brand" /> : null}
    </div>
  );
};
